import { EMPTY_SCHEDULE, openState, type Schedule, type Shift, scheduleText, sentence } from "@orbis/shared";
import { useState } from "react";
import { useToast } from "../../components/ui";
import { ApiError } from "../../lib/api";
import { useCompany, useUpdateCompany } from "../../lib/queries";

/** Monday first, as people read a week; values are Date.getDay(). */
export const DAYS = [
  { day: 1, label: "Lunes", short: "L" },
  { day: 2, label: "Martes", short: "K" },
  { day: 3, label: "Miércoles", short: "M" },
  { day: 4, label: "Jueves", short: "J" },
  { day: 5, label: "Viernes", short: "V" },
  { day: 6, label: "Sábado", short: "S" },
  { day: 0, label: "Domingo", short: "D" },
];
const MAX_SHIFTS = 4;
export const WEEKDAYS = [1, 2, 3, 4, 5];

/** Shifts as editable rows: a start, an end, remove; "Agregar turno" for split shifts. */
export function Shifts({
  shifts,
  label,
  onChange,
  emptyLabel = "Abrir este día",
}: {
  shifts: Shift[];
  label: string;
  onChange: (s: Shift[]) => void;
  /** The button when there are no shifts yet. */
  emptyLabel?: string;
}) {
  return (
    <div className="shifts">
      {shifts.map((s, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: shifts are edited in place
        <div className="shift" key={i}>
          <input
            className="input"
            type="time"
            aria-label={`${label}, turno ${i + 1}: desde`}
            value={s.from}
            onChange={(e) => onChange(shifts.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))}
          />
          <span className="faint">a</span>
          <input
            className="input"
            type="time"
            aria-label={`${label}, turno ${i + 1}: hasta`}
            value={s.to === "24:00" ? "23:59" : s.to}
            onChange={(e) => onChange(shifts.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))}
          />
          <button
            className="btn btn-sm"
            type="button"
            aria-label={`Quitar el turno ${i + 1} del ${label.toLowerCase()}`}
            onClick={() => onChange(shifts.filter((_, j) => j !== i))}
          >
            Quitar
          </button>
        </div>
      ))}
      {shifts.length < MAX_SHIFTS && (
        <button
          className="btn btn-sm btn-ghost"
          type="button"
          onClick={() => {
            const last = shifts.at(-1);
            // A second shift starts an hour after the first ends (the usual lunch break).
            const from = last ? addHour(last.to) : "08:00";
            onChange([...shifts, { from, to: last ? addHour(from, 4) : "17:00" }]);
          }}
        >
          {shifts.length ? "Agregar turno" : emptyLabel}
        </button>
      )}
    </div>
  );
}

const addHour = (hhmm: string, hours = 1) => {
  const [h = 0, m = 0] = hhmm.split(":").map(Number);
  return `${String(Math.min(23, h + hours)).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** Shifts the server accepts: both times, the end after the start. */
export const valid = (shifts: Shift[]) => shifts.every((s) => s.from && s.to && s.from < s.to);

/**
 * The company's opening hours (f4-08). Lumi reads them to know whether it is open now,
 * and when it is closed it tells the customer when a person answers. {{horario}} is
 * written from them.
 */
export function ScheduleCard() {
  const company = useCompany();
  const update = useUpdateCompany();
  const toast = useToast();
  const saved = company.data?.schedule ?? null;
  const [draft, setDraft] = useState<Schedule | null>(null);
  const s = draft ?? saved ?? EMPTY_SCHEDULE;
  const set = (next: Schedule) => setDraft(next);
  const [quickDays, setQuickDays] = useState<number[]>(WEEKDAYS);
  const [quick, setQuick] = useState<Shift[]>([{ from: "08:00", to: "17:00" }]);

  const allValid = s.week.every(valid) && s.special.every((d) => d.date && valid(d.shifts));
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved ?? EMPTY_SCHEDULE);
  const now = openState(s);

  async function save() {
    try {
      await update.mutateAsync({ schedule: s });
      setDraft(null);
      toast("Horario guardado");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "No se pudo guardar el horario.");
    }
  }

  if (company.isPending) return <p className="muted">Cargando…</p>;

  return (
    <>
      <div className="card">
        <div className="card-body schedule">
          <div className="account-head">
            <div>
              <h3>Horario de atención</h3>
              <p className="muted">
                Lumi sabe si están abiertos y, fuera de horario, avisa cuándo responde un asesor. La variable{" "}
                <span className="mono">{"{{horario}}"}</span> se escribe sola.
              </p>
            </div>
          </div>
          {!saved && company.data?.hours && (
            <p className="faint">
              Horario guardado como texto: “{company.data.hours}”. Se reemplaza al guardar el horario por días.
            </p>
          )}
          <label className="check">
            <input
              type="checkbox"
              checked={s.alwaysOpen}
              onChange={(e) => set({ ...s, alwaysOpen: e.target.checked })}
            />
            Abierto las 24 horas, todos los días
          </label>

          {!s.alwaysOpen && (
            <>
              <fieldset className="schedule-quick">
                <legend>Configuración rápida</legend>
                <div className="day-chips">
                  {DAYS.map((d) => (
                    <button
                      key={d.day}
                      type="button"
                      aria-pressed={quickDays.includes(d.day)}
                      aria-label={d.label}
                      title={d.label}
                      onClick={() =>
                        setQuickDays((q) => (q.includes(d.day) ? q.filter((x) => x !== d.day) : [...q, d.day]))
                      }
                    >
                      {d.short}
                    </button>
                  ))}
                </div>
                {/* Every shift here (a split day too) goes to each marked day. */}
                <Shifts shifts={quick} label="Configuración rápida" onChange={setQuick} emptyLabel="Agregar turno" />
                <div>
                  <button
                    className="btn btn-sm"
                    type="button"
                    disabled={!quickDays.length || !quick.length || !valid(quick)}
                    onClick={() =>
                      set({
                        ...s,
                        week: s.week.map((shifts, day) =>
                          quickDays.includes(day) ? quick.map((q) => ({ ...q })) : shifts,
                        ),
                      })
                    }
                  >
                    Aplicar a los días marcados
                  </button>
                </div>
              </fieldset>

              <ul className="schedule-week">
                {DAYS.map((d) => {
                  const shifts = s.week[d.day] ?? [];
                  return (
                    <li key={d.day}>
                      <span className="schedule-day">{d.label}</span>
                      {shifts.length === 0 && <span className="pill pill-closed">Cerrado</span>}
                      <Shifts
                        shifts={shifts}
                        label={d.label}
                        onChange={(next) => set({ ...s, week: s.week.map((x, i) => (i === d.day ? next : x)) })}
                      />
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </div>

      {!s.alwaysOpen && (
        <div className="card">
          <div className="card-body schedule">
            <h3>Días especiales</h3>
            <p className="muted">
              Feriados, cierres o días con otro horario. Ese día manda sobre el horario de la semana.
            </p>
            {s.special.length > 0 && (
              <ul className="schedule-week">
                {s.special.map((d, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: rows are edited in place
                  <li key={i}>
                    <div className="special-head">
                      <input
                        className="input"
                        type="date"
                        aria-label="Fecha"
                        value={d.date}
                        onChange={(e) =>
                          set({
                            ...s,
                            special: s.special.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)),
                          })
                        }
                      />
                      <input
                        className="input"
                        aria-label="Motivo"
                        placeholder="Ej.: Feriado, Inventario"
                        maxLength={60}
                        value={d.label ?? ""}
                        onChange={(e) =>
                          set({
                            ...s,
                            special: s.special.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                          })
                        }
                      />
                      <button
                        className="btn btn-sm"
                        type="button"
                        onClick={() => set({ ...s, special: s.special.filter((_, j) => j !== i) })}
                      >
                        Quitar
                      </button>
                    </div>
                    {d.shifts.length === 0 && <span className="pill pill-closed">Cerrado todo el día</span>}
                    <Shifts
                      shifts={d.shifts}
                      label={d.label || d.date}
                      onChange={(next) =>
                        set({ ...s, special: s.special.map((x, j) => (j === i ? { ...x, shifts: next } : x)) })
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
            <div>
              <button
                className="btn btn-sm"
                type="button"
                disabled={s.special.length >= 60}
                onClick={() => set({ ...s, special: [...s.special, { date: "", label: "", shifts: [] }] })}
              >
                Agregar día especial
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h3>Así lo dice Lumi</h3>
          <p>{sentence(scheduleText(s))}</p>
          <p className="faint">
            {sentence(
              now.open
                ? `Ahora: abierto${now.closesAt ? `, cierra a las ${now.closesAt}` : ""}`
                : `Ahora: cerrado${now.opensText ? `, abre ${now.opensText}` : ""}`,
            )}
          </p>
        </div>
      </div>

      {dirty && (
        <div className="product-save">
          <button className="btn" type="button" onClick={() => setDraft(null)}>
            Descartar
          </button>
          <button className="btn btn-primary" type="button" disabled={!allValid || update.isPending} onClick={save}>
            {update.isPending ? "Guardando…" : allValid ? "Guardar horario" : "Revisa los turnos"}
          </button>
        </div>
      )}
    </>
  );
}
